import { MouseEvent, useState } from "react";
import { Search, Store } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useGetStoresFilterableOptionsQuery } from "@/services/store/select-options";
import { useStoreMultiFilter } from "./store-filter/use-store-multi-filter";
import { useStoreSelectionDraft } from "./store-filter/use-store-selection-draft";
import { StoreFilterTrigger } from "./store-filter/store-filter-trigger";
import { StoreFilterChips } from "./store-filter/store-filter-chips";
import { StoreFilterStatusBar } from "./store-filter/store-filter-status-bar";
import { StoreFilterList } from "./store-filter/store-filter-list";
import { StoreFilterFooter } from "./store-filter/store-filter-footer";
import {
  STORE_FILTER_DIMENSION_LABELS,
  STORE_FILTER_TEXT,
} from "./store-filter/constants";
import { StoreFilterDimension } from "./store-filter/types";

interface CampaignStoreFilterProps {
  selectedStores: number[];
  onApply: (storeCodes: number[]) => void;
}

/**
 * STORY-NEW-043: Filtro de lojas (multi-seleção) da listagem de campanhas.
 * Painel único e plano: busca + chips de Estado/Regional (dropdown flutuante, um
 * aberto por vez) + lista com checkbox + footer Aplicar. Orquestra os subcomponentes
 * de `./store-filter`; a lógica vive nos hooks `useStoreMultiFilter`/`useStoreSelectionDraft`.
 */
export function CampaignStoreFilter({
  selectedStores,
  onApply,
}: CampaignStoreFilterProps) {
  const [open, setOpen] = useState(false);

  const { data: storesData, isLoading } = useGetStoresFilterableOptionsQuery();
  const filter = useStoreMultiFilter(storesData || []);
  const draft = useStoreSelectionDraft({
    committed: selectedStores,
    filteredStores: filter.filteredStores,
    isOpen: open,
  });

  const dimensions: StoreFilterDimension[] = [
    {
      id: "estado",
      label: STORE_FILTER_DIMENSION_LABELS.estado,
      summary: filter.statesSummary,
      onClear: filter.clearStates,
      options: filter.stateOptions.map(option => ({
        key: option.value,
        label: option.label,
        checked: filter.selectedStates.includes(option.value),
        onToggle: () => filter.toggleState(option.value),
      })),
    },
    {
      id: "regional",
      label: STORE_FILTER_DIMENSION_LABELS.regional,
      summary: filter.regionalsSummary,
      onClear: filter.clearRegionals,
      options: filter.regionalOptions.map(option => ({
        key: String(option.value),
        label: option.label,
        checked: filter.selectedRegionals.includes(option.value),
        onToggle: () => filter.toggleRegional(option.value),
      })),
    },
  ];

  const handleHeaderClear = () => {
    draft.clear();
    filter.clearAllFilters();
  };

  const handleCancel = () => {
    draft.reset();
    setOpen(false);
  };

  const handleApply = () => {
    onApply(draft.staged);
    setOpen(false);
  };

  const handleClearApplied = (event: MouseEvent) => {
    event.stopPropagation();
    onApply([]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <StoreFilterTrigger
          appliedCount={selectedStores.length}
          onClearApplied={handleClearApplied}
        />
      </PopoverTrigger>
      <PopoverContent className="w-[340px] p-0" align="start">
        <div className="flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b px-3 py-2.5">
            <div className="flex items-center gap-2">
              <Store className="h-4 w-4 text-primary" />
              <p className="text-sm font-medium">{STORE_FILTER_TEXT.title}</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground"
              onClick={handleHeaderClear}
            >
              {STORE_FILTER_TEXT.clear}
            </Button>
          </div>

          <div className="space-y-3 p-3">
            {/* Busca */}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={STORE_FILTER_TEXT.searchPlaceholder}
                value={filter.searchTerm}
                onChange={event => filter.setSearchTerm(event.target.value)}
                className="h-8 pl-8"
              />
            </div>

            {/* Chips Estado/Regional */}
            <StoreFilterChips dimensions={dimensions} />

            {/* Barra de status */}
            <StoreFilterStatusBar
              totalCount={filter.filteredStores.length}
              selectedCount={draft.staged.length}
              allSelected={draft.allFilteredStaged}
              onToggleAll={draft.toggleAllFiltered}
            />

            {/* Lista */}
            <StoreFilterList
              stores={filter.filteredStores}
              isLoading={isLoading}
              isSelected={code => draft.staged.includes(code)}
              onToggle={draft.toggle}
              emptyLabel={STORE_FILTER_TEXT.empty}
              loadingLabel={STORE_FILTER_TEXT.loading}
            />
          </div>

          {/* Footer */}
          <StoreFilterFooter
            selectedCount={draft.staged.length}
            onCancel={handleCancel}
            onApply={handleApply}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
